import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { usersApi } from '@/lib/api';

// Android only until the App Store binary includes expo-notifications.
// iOS production is still runtime 1.0.4; loading the native module there
// crashes the process on launch.
const NATIVE_PUSH_PLATFORMS = new Set(['android']);

// Android 1.0.5 (versionCode 110) does not ship expo-notifications. Importing
// that native module on those binaries crashes the process — blank screen on
// launch. 1.0.6+ includes the module and can register for push.
export const ANDROID_PUSH_MIN_NATIVE_VERSION = '1.0.6';

let rememberedPushToken = null;

export function getRememberedPushToken() {
  return rememberedPushToken;
}

export function rememberPushToken(token) {
  rememberedPushToken = token || null;
}

export function compareAppVersions(a, b) {
  const left = String(a || '').split('.').map((part) => parseInt(part, 10) || 0);
  const right = String(b || '').split('.').map((part) => parseInt(part, 10) || 0);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const delta = (left[i] || 0) - (right[i] || 0);
    if (delta) return delta;
  }
  return 0;
}

export function nativeVersionSupportsPush(nativeVersion, runtimeVersion) {
  const versions = [nativeVersion, runtimeVersion].filter(Boolean);
  if (!versions.length) return false;
  // Disable if the binary or this OTA runtime is older than 1.0.6. 1.0.5
  // devices can report a newer JS config version after an OTA; either
  // signal below 1.0.6 means expo-notifications is not in the binary.
  return versions.every((version) => (
    compareAppVersions(version, ANDROID_PUSH_MIN_NATIVE_VERSION) >= 0
  ));
}

function getRuntimeVersion() {
  const runtime = Constants.expoConfig?.runtimeVersion;
  return typeof runtime === 'string' ? runtime : null;
}

export function isNativePushPlatform(platform = Platform.OS) {
  if (!NATIVE_PUSH_PLATFORMS.has(platform)) return false;
  return nativeVersionSupportsPush(Constants.nativeAppVersion, getRuntimeVersion());
}

function getEasProjectId() {
  return Constants.easConfig?.projectId
    || Constants.expoConfig?.extra?.eas?.projectId
    || null;
}

export async function registerDevicePushToken() {
  if (!isNativePushPlatform()) return null;

  const Notifications = await import('expo-notifications');
  const Device = await import('expo-device');

  if (!Device.isDevice) return null;

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.DEFAULT,
    });
  }

  const existing = await Notifications.getPermissionsAsync();
  let status = existing.status;
  if (status !== 'granted') {
    const requested = await Notifications.requestPermissionsAsync();
    status = requested.status;
  }
  if (status !== 'granted') return null;

  const projectId = getEasProjectId();
  if (!projectId) {
    console.warn('[push] missing EAS projectId; skip token registration');
    return null;
  }

  const tokenResult = await Notifications.getExpoPushTokenAsync({ projectId });
  const token = tokenResult?.data;
  if (!token) return null;

  rememberPushToken(token);
  await usersApi.registerPushToken({ token, platform: Platform.OS });
  return token;
}

export async function unregisterRememberedPushToken() {
  const token = rememberedPushToken;
  rememberPushToken(null);
  if (!token) return;
  try {
    await usersApi.unregisterPushToken(token);
  } catch (error) {
    console.warn('[push] unregister failed', error?.message || error);
  }
}
